import { useState, useEffect, useRef } from "react";
import { useKeycloak } from "@react-keycloak/web";
import { useNavigate } from 'react-router-dom';
import Swal from "sweetalert2";

import { HiOutlineLockClosed } from "react-icons/hi2";
import { RiAttachment2 } from "react-icons/ri";
import { GoArrowUpRight } from "react-icons/go";
import { FaEllipsis, FaCircle, FaCheck } from "react-icons/fa6";
import { TbBolt } from "react-icons/tb";


import LogoMJV from "../../../../../assets/img/logo-mjv-aira-star.svg";

import KnowledgeBaseService from "../../../../../services/KnowledgeBase/KnowledgeBaseService";
import WorkspaceService from "../../../../../services/Workspace/WorkspaceService";
import PublishableService from "../../../../../services/Publishable/PublishableService";
import { getTimeAgo } from "../../../../../utils/sharedFunctions";

import ButtonMain from "../../../../Atoms/ButtonMain";
import Loader from "../../../../Atoms/Loader";
import ToggleButton from "../../../../Atoms/Toggle";

import ActionsDropdown from "./Molecules/ActionsDropdown";
import WorkspaceActionsDropdown from "./Molecules/WorkspaceActionsDropdown";
import AttachmentDropdown from "./Molecules/AttachmentDropdown";
import RenameModal from "./Molecules/RenameModal";
import MemberAvatar from "../../../../Molecules/MemberAvatar";

import DeleteFileModal from "./Parts/DeleteFileModal";
import DeleteWorkspaceModal from "./Parts/DeleteWorkspaceModal";
import PublishModal from "./Molecules/PublishModal";
import MembersList from "../MembersList";

import './styles.css';

export default function FileListTabs({
    projectId,
    knowledgeBaseFiles,
    loadKnowledgeBaseFiles,
    downloadFileFunction,
    renameFileFunction,
    workspaceItems,
    loadWorkspaceItems,
    projectName,
    companyName,
    membersReloadKey,
}) {
    const { keycloak, initialized } = useKeycloak();
    const navigate = useNavigate();
    const KEYCLOACK_TOKEN = keycloak.token;

    const [files, setFiles] = useState([]);
    const [workspaceActive, setWorkspaceActive] = useState(true);
    const [knowledgeBaseActive, setKnowledgeBaseActive] = useState(false);
    const [membersActive, setMembersActive] = useState(false);
    const [openDropdownId, setOpenDropdownId] = useState(null);
    const [openWorkspaceDropdownId, setOpenWorkspaceDropdownId] = useState(null);
    const [showAttachDropdown, setShowAttachDropdown] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [renameTarget, setRenameTarget] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [keycloakTokenLoaded, setKeycloackTokenLoaded] = useState(null);
    const [loadingDeleting, setLoadingDeleting] = useState(false);
    const [loadingDeletingWorkspace, setLoadingDeletingWorkspace] = useState(false);
    const [loadingRenaming, setLoadingRenaming] = useState(false);
    const [hoveredFileId, setHoveredFileId] = useState(null);
    const [workspaceFilter, setWorkspaceFilter] = useState('all');
    const [workspaceItemList, setWorkspaceItemList] = useState([]);
    const [deleteWorkspaceTarget, setDeleteWorkspaceTarget] = useState(null);
    const [showPublishModal, setShowPublishModal] = useState(false);
    const [publishTarget, setPublishTarget] = useState(null);

    const fileInputRef = useRef(null);
    const workspaceDropdownButtonRefs = useRef({});
    const actionsDropdownButtonRefs = useRef({});

    const [knowledgeBaseFilter, setKnowledgeBaseFilter] = useState('all');

    useEffect(() => {
        if (workspaceFilter === 'all') {
            setWorkspaceItemList(workspaceItems.outputs);
        } else if (workspaceFilter === 'completed') {
            setWorkspaceItemList(workspaceItems.outputs.filter(item => item.status === 'FINISHED'));
        } else if (workspaceFilter === 'editing') {
            setWorkspaceItemList(workspaceItems.outputs.filter(item => item.status === 'IN_PROGRESS'));
        }
    }, [workspaceFilter, workspaceItems]);

    useEffect(() => {
        if (knowledgeBaseFilter === 'generated') {
            setFiles(knowledgeBaseFiles.filter(file => file.origin !== 'ATTACHMENT'));
        } else if (knowledgeBaseFilter === 'attachments') {
            setFiles(knowledgeBaseFiles.filter(file => file.origin === 'ATTACHMENT'));
        } else {
            setFiles(knowledgeBaseFiles);
        }
    }, [knowledgeBaseFilter, knowledgeBaseFiles]);

    useEffect(() => {
        setKeycloackTokenLoaded(KEYCLOACK_TOKEN);
    }, [KEYCLOACK_TOKEN, initialized]);

    const FileListTabsStyles = {
        divider: {
            borderTop: '1px solid #314158',
            margin: '20px 0',
            height: '1px',
        },
        headerContainer: {
            display: 'flex',
            gap: '20px',
            marginBottom: '20px',
            borderBottom: '1px solid #314158',
        },
        knowledgeBaseButton: {
            fontWeight: 'bold',
            borderBottom: knowledgeBaseActive ? '2px solid #8E51FF' : 'none',
            borderRadius: '0px',
        },
        workspaceButton: {
            fontWeight: 'bold',
            borderBottom: workspaceActive ? '2px solid #8E51FF' : 'none',
            borderRadius: '0px',
        },
        membersButton: {
            fontWeight: 'bold',
            borderBottom: membersActive ? '2px solid #8E51FF' : 'none',
            borderRadius: '0px',
        },
        fileListItem: {
            padding: '3px 0px',
            margin: '8px 0',
            borderRadius: '8px',
            transition: 'all 0.2s ease-in-out',
        },
        fileListItemHover: {
            backgroundColor: '#1A1D29',
        }
    }

    const handleDeleteActionSelected = (file) => {
        setDeleteTarget(file);
        setOpenDropdownId(null);
    };

    const handleDeleteConfirm = async () => {
        try {
            setLoadingDeleting(true);
            await KnowledgeBaseService.deleteFile(deleteTarget.id, keycloakTokenLoaded);
            await loadKnowledgeBaseFiles();
            Swal.fire({
                showConfirmButton: false,
                title: "Arquivo deletado com sucesso!",
                text: "O arquivo foi deletado com sucesso.",
                background: '#27272A',
                color: '#fff',
            });
        } catch (error) {
            Swal.fire({
                showConfirmButton: false,
                title: "Erro ao deletar arquivo",
                text: "Ocorreu um erro ao deletar o arquivo. Tente novamente.",
                background: '#27272A',
                color: '#fff',
            });
        } finally {
            setDeleteTarget(null);
            setLoadingDeleting(false);
        }
    };

    const handleRenameClick = (file) => {
        setRenameTarget(file);
        setOpenDropdownId(null);
    };

    const handleRenameConfirm = async (newName) => {
        setLoadingRenaming(true);
        await renameFileFunction(renameTarget.id, newName);

        setRenameTarget(null);
        setLoadingRenaming(false);
    };

    const handleDeleteWorkspaceActionSelected = (workspaceItemId) => {
        setDeleteWorkspaceTarget(workspaceItemId.id);
        setOpenWorkspaceDropdownId(null);
    };

    const handleUploadFile = () => {
        fileInputRef.current?.click();
    };

    const handleFileSelect = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const allowedTypes = ['.txt', '.pdf', '.docx'];
        const fileExtension = '.' + file.name.split('.').pop().toLowerCase();

        if (!allowedTypes.includes(fileExtension)) {
            Swal.fire({
                icon: "error",
                title: "Tipo de arquivo não permitido",
                text: "Por favor, selecione um arquivo .txt, .pdf ou .docx",
                background: '#27272A',
                color: '#fff',
            });

            event.target.value = '';
            return;
        }

        const maxSize = 100 * 1024 * 1024; // 100MB in bytes
        if (file.size > maxSize) {
            Swal.fire({
                icon: "error",
                title: "Arquivo muito grande",
                text: "O arquivo deve ter no máximo 100MB",
                background: '#27272A',
                color: '#fff',
            });

            event.target.value = '';
            return;
        }

        if (!projectId) {
            Swal.fire({
                title: "Erro",
                text: "ID do projeto não encontrado",
                background: '#27272A',
                color: '#fff',
            });
            return;
        }

        try {
            setIsUploading(true);

            await KnowledgeBaseService.uploadFile(file, projectId, keycloakTokenLoaded);
            await loadKnowledgeBaseFiles();

            Swal.fire({
                title: "Arquivo enviado com sucesso!",
                text: `${file.name} foi adicionado à base de conhecimento`,
                background: '#27272A',
                color: '#fff',
                timer: 3000,
                showConfirmButton: false
            });

        } catch (error) {
            Swal.fire({
                title: "Erro ao enviar arquivo",
                text: error.message || "Ocorreu um erro ao enviar o arquivo. Tente novamente.",
                background: '#27272A',
                color: '#fff',
            });
        } finally {
            setIsUploading(false);
            event.target.value = '';
        }
    };

    const handleDownloadFile = async (fileId) => {
        downloadFileFunction(fileId);
    }

    const handleRemovePublishable = async (file) => {
        setOpenDropdownId(null);

        const result = await Swal.fire({
            title: "Remover de publicados?",
            text: "Deseja remover este arquivo dos publicados?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Sim, remover",
            cancelButtonText: "Cancelar",
            background: '#27272A',
            color: '#fff',
        });

        if (!result.isConfirmed) return;

        Swal.fire({
            title: "Removendo publicação...",
            text: "Aguarde enquanto o arquivo é removido dos publicados.",
            background: '#27272A',
            color: '#fff',
            allowOutsideClick: false,
            allowEscapeKey: false,
            showConfirmButton: false,
            didOpen: () => Swal.showLoading(),
        });

        try {
            await PublishableService.deletePublishableById(file.publishable.id, keycloakTokenLoaded);
            await loadKnowledgeBaseFiles();
            Swal.fire({
                title: "Removido dos publicados!",
                text: "O arquivo foi removido dos publicados com sucesso.",
                background: '#27272A',
                color: '#fff',
                showConfirmButton: false,
                timer: 1800,
            });
        } catch (error) {
            Swal.fire({
                icon: "error",
                title: "Erro ao remover publicação",
                text: error.message || "Ocorreu um erro ao remover o arquivo dos publicados. Tente novamente.",
                background: '#27272A',
                color: '#fff',
                showConfirmButton: false,
            });
        }
    }

    const handleDeleteWorkspaceItemConfirm = async () => {
        try {
            setLoadingDeletingWorkspace(true);
            await WorkspaceService.deleteWorkspaceItem(deleteWorkspaceTarget, keycloakTokenLoaded);

            if (loadWorkspaceItems) {
                await loadWorkspaceItems();
            }

            Swal.fire({
                title: "Trabalho deletado com sucesso!",
                text: "O trabalho foi deletado com sucesso.",
                background: '#27272A',
                color: '#fff',
                showConfirmButton: false,
                timer: 1800,
            });

        } catch (error) {
            console.error('Error deleting workspace item:', error);
            Swal.fire({
                icon: "error",
                title: "Erro ao deletar trabalho",
                text: "Ocorreu um erro ao deletar o trabalho. Tente novamente.",
                background: '#27272A',
                color: '#fff',
                showConfirmButton: false,
            });
        } finally {
            setDeleteWorkspaceTarget(null);
            setLoadingDeletingWorkspace(false);
        }
        
    }

    const handleDownloadWorkspaceItem = async (workspaceItemId) => {
        alert('Download do item do workspace. Em breve')
    }

    const handleAccessWorkspaceItem = async (workspaceItem) => {
        if (workspaceItem.tool === 'BRAINSTORM') {
            const outputId = workspaceItem.id;

            const ideasArray = Object.values(workspaceItem.data.ideas);
            const hasConcepts = ideasArray.filter(idea => idea.concept);
            if (hasConcepts.length > 0) {
                navigate(`/brainstorm/concepts/${projectId}/${outputId}`);
            } else {
                navigate(`/brainstorm/ideas/${projectId}/${outputId}`);
            }
        } else {
            alert('Ferramenta não encontrada. Em breve você terá acesso a esta e muitas outras!')
        }
    }

    const formatWorkspaceItemStatus = (status) => {
        if (status === 'FINISHED') return (
            <div className="d-flex flex-row align-items-center gap-2">
                <FaCircle size={10} color="#05DF72" /> <p>Concluído</p>
            </div>
        );
        if (status === 'IN_PROGRESS') return (
            <div className="d-flex flex-row align-items-center gap-2">
                <FaCircle size={10} color="#EDEDED" /> <p>Em edição</p>
            </div>
        );
        if (status === 'DRAFT') return (
            <div className="d-flex flex-row align-items-center gap-2">
                <FaCircle size={10} color="#51A2FF" /> <p>Rascunho</p>
            </div>
        );
        return status;
    }

    const formatKnowledgeBaseItemStatus = (status) => {
        if (status === 'BRAINSTORM') return (
            <div className="d-flex flex-row align-items-center gap-2">
                <TbBolt size={18} color="#A3B3FF" /> <p>Brainstorm</p>
            </div>
        );
        if (status === 'ATTACHMENT') return (
            <div className="d-flex flex-row align-items-center gap-2">
                <RiAttachment2 size={18} color="#A3B3FF" /> <p>Anexo</p>
            </div>
        );
        return status;
    }

    const handlePublishClick = (file) => {
        setPublishTarget(file);
        setShowPublishModal(true);
    };

    const handleClosePublishModal = () => {
        setShowPublishModal(false);
        setPublishTarget(null);
    };

    return (
        <div>
            <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept=".txt,.pdf,.docx"
                style={{ display: 'none' }}
            />

            <DeleteFileModal
                isOpen={deleteTarget !== null}
                onClose={() => setDeleteTarget(null)}
                title={deleteTarget?.name || ''}
                onConfirm={handleDeleteConfirm}
                isLoading={loadingDeleting}
            />

            <DeleteWorkspaceModal
                isOpen={deleteWorkspaceTarget !== null}
                onClose={() => setDeleteWorkspaceTarget(null)}
                onConfirm={handleDeleteWorkspaceItemConfirm}
                isLoading={loadingDeletingWorkspace}
            />

            <RenameModal
                isOpen={renameTarget !== null}
                onClose={() => setRenameTarget(null)}
                onConfirm={handleRenameConfirm}
                currentName={renameTarget?.name || ''}
                isLoading={loadingRenaming}
            />

            <PublishModal
                show={showPublishModal}
                onHide={handleClosePublishModal}
                file={publishTarget}
                projectId={projectId}
                projectName={projectName}
                companyName={companyName}
                onPublishSuccess={loadKnowledgeBaseFiles}
            />

            <div style={FileListTabsStyles.headerContainer}>
                <button
                    className="btn"
                    onClick={() => {
                        setWorkspaceActive(true);
                        setKnowledgeBaseActive(false);
                        setMembersActive(false);
                    }}
                    style={FileListTabsStyles.workspaceButton}
                >
                    Workspace
                </button>

                <button
                    className="btn"
                    onClick={() => {
                        setWorkspaceActive(false);
                        setKnowledgeBaseActive(true);
                        setMembersActive(false);
                    }}
                    style={FileListTabsStyles.knowledgeBaseButton}
                >
                    Base de conhecimento
                </button>

                <button
                    className="btn"
                    onClick={() => {
                        setWorkspaceActive(false);
                        setKnowledgeBaseActive(false);
                        setMembersActive(true);
                    }}
                    style={FileListTabsStyles.membersButton}
                >
                    Membros
                </button>
            </div>

            <div>
                {workspaceActive && (
                    <>
                        <p className="text-secondary mb-3">Trabalhos em andamento e concluídos. Ao serem concluídos, os trabalhos podem ser adicionados à base de conhecimento.</p>

                        <div className="d-flex flex-row align-items-center justify-content-between">
                            <div className="d-flex flex-row align-items-center gap-2">
                                <button
                                    className="btn btn-primary d-flex flex-row gap-2 align-items-center" onClick={() => setWorkspaceFilter('all')}
                                    style={workspaceFilter === 'all' ? { backgroundColor: '#C27AFF26', color: 'white', border: '1px solid #4D179A', borderRadius: '8px', fontWeight: '500' } : {}}
                                >
                                    Todos
                                </button>
                                <button className="btn btn-primary d-flex flex-row gap-2 align-items-center" onClick={() => setWorkspaceFilter('editing')}
                                    style={workspaceFilter === 'editing' ? { backgroundColor: '#C27AFF26', color: 'white', border: '1px solid #4D179A', borderRadius: '8px', fontWeight: '500' } : {}}
                                >
                                    Editando
                                </button>
                                <button className="btn btn-primary d-flex flex-row gap-2 align-items-center" onClick={() => setWorkspaceFilter('completed')}
                                    style={workspaceFilter === 'completed' ? { backgroundColor: '#C27AFF26', color: 'white', border: '1px solid #4D179A', borderRadius: '8px', fontWeight: '500' } : {}}
                                >
                                    Concluído
                                </button>
                            </div>
                        </div>

                        <div style={FileListTabsStyles.divider}></div>

                        {workspaceItemList?.length === 0 && (
                            <p className="text-secondary">Nenhum arquivo encontrado no workspace</p>
                        )}

                        <div className="list-scroll-items">
                            {workspaceItemList?.map((item) => (
                                <div
                                    key={item.data.id}
                                    id="file-list-item"
                                    className="row align-items-center"
                                    style={{
                                        ...FileListTabsStyles.fileListItem,
                                        ...(hoveredFileId === item.data.id ? FileListTabsStyles.fileListItemHover : {})
                                    }}
                                    onMouseEnter={() => setHoveredFileId(item.data.id)}
                                    onMouseLeave={() => setHoveredFileId(null)}
                                >
                                    <div className="col-12 col-md-4">
                                        <p>{item.tool}</p>
                                        <div className="d-flex flex-row align-items-center gap-2">
                                            <p style={{ color: '#A3B3FF' }}>
                                                {item.tool === 'BRAINSTORM' ? 'Brainstorm' : item.tool.toLowerCase()}
                                            </p>
                                            <p>{formatWorkspaceItemStatus(item.status)}</p>
                                        </div>
                                    </div>

                                    <div
                                        className="col-12 col-md-4 my-3 my-md-0"
                                        style={{ borderRight: '1px solid #314158', position: 'relative', display: 'flex', justifyContent: 'end' }}
                                    >
                                        <div className="d-flex flex-column my-0">
                                            {item.status === 'FINISHED' && (
                                                <ToggleButton
                                                    isActive={false}
                                                    onToggle={() => alert('Em breve, enviará o entregável do trabalho para a base de conhecimento')}
                                                />
                                            )}

                                            {item.status === 'IN_PROGRESS' && (
                                                <p className="text-secondary mb-2 d-flex align-items-center">
                                                    <HiOutlineLockClosed color="#ededed" /> Bloqueado
                                                </p>
                                            )}


                                            {item.status === 'DRAFT' && (
                                                <button className='draft-button'>Continuar</button>
                                            )}

                                            <button
                                                ref={el => workspaceDropdownButtonRefs.current[item.data.id] = el}
                                                onClick={() => setOpenWorkspaceDropdownId(openWorkspaceDropdownId === item.data.id ? null : item.data.id)}
                                                style={{ position: 'relative', textAlign: 'right', backgroundColor: 'transparent', border: 'none', color: '#FFF' }}
                                            >
                                                <FaEllipsis />
                                            </button>

                                            <WorkspaceActionsDropdown
                                                isOpen={openWorkspaceDropdownId === item.data.id}
                                                onClose={() => setOpenWorkspaceDropdownId(null)}
                                                onDeleteClick={() => handleDeleteWorkspaceActionSelected(item)}
                                                onRenameClick={() => handleRenameClick(item)}
                                                onDownloadClick={() => handleDownloadWorkspaceItem(item.data.id)}
                                                onAccessClick={() => handleAccessWorkspaceItem(item)}
                                                status={item.status}
                                                buttonRef={workspaceDropdownButtonRefs.current[item.data.id]}
                                            />
                                        </div>
                                    </div>

                                    <div className="col-12 col-md-4">
                                        <div className="d-flex flex-row gap-2 align-items-center mb-1">
                                            <MemberAvatar />
                                            <p className="mb-0">{item.processProfile.user.username}</p>
                                        </div>

                                        <p className="text-secondary mb-0" style={{ marginLeft: '38px' }}>{getTimeAgo(item.updatedAt)}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                        
                    </>
                )}

                {knowledgeBaseActive && (
                    <>
                        <p className="text-secondary mb-3">Trabalhos e anexos na Base de conhecimento podem ser publicados como entregáveis para o cliente.</p>

                        <div className="row">
                            <div id="filter-button-container" className="col-12 col-md-6 mb-3 mb-md-0">
                                <div className="d-flex flex-row align-items-center gap-2 justify-content-center justify-content-md-start">
                                    <button
                                        onClick={() => setKnowledgeBaseFilter('all')}
                                        className="btn btn-primary d-flex flex-row gap-2 align-items-center"
                                        style={knowledgeBaseFilter === 'all' ? { backgroundColor: '#C27AFF26', color: 'white', border: '1px solid #4D179A', borderRadius: '8px', fontWeight: '500' } : {}}
                                    >
                                        Todos
                                    </button>
                                    <button
                                        onClick={() => setKnowledgeBaseFilter('generated')}
                                        className="btn btn-primary d-flex flex-row gap-2 align-items-center"
                                        style={knowledgeBaseFilter === 'generated' ? { backgroundColor: '#C27AFF26', color: 'white', border: '1px solid #4D179A', borderRadius: '8px', fontWeight: '500' } : {}}
                                    >
                                        <img src={LogoMJV} alt="Logo MJV" className="logo-mjv" width={15} height={15} />
                                        Gerado na Aira
                                    </button>
                                    <button
                                        onClick={() => setKnowledgeBaseFilter('attachments')}
                                        className="btn btn-primary d-flex flex-row gap-2 align-items-center"
                                        style={knowledgeBaseFilter === 'attachments' ? { backgroundColor: '#C27AFF26', color: 'white', border: '1px solid #4D179A', borderRadius: '8px', fontWeight: '500' } : {}}
                                    >
                                        <RiAttachment2 />
                                        Anexos
                                    </button>
                                </div>
                            </div>

                            <div id="attach-button-container" className="col-12 col-md-6">
                                <div className="d-flex flex-row align-items-center gap-2 justify-content-center justify-content-md-end">
                                    <div style={{ position: 'relative' }}>
                                        {isUploading ? (
                                            <Loader />
                                        ) : (
                                            <button
                                                className="btn d-flex flex-row gap-2 align-items-center"
                                                onClick={handleUploadFile}
                                            >
                                                <RiAttachment2 />
                                                Anexar arquivo
                                            </button>
                                        )}

                                        <AttachmentDropdown
                                            isOpen={showAttachDropdown}
                                            onClose={() => setShowAttachDropdown(false)}
                                            onUploadClick={handleUploadFile}
                                            isUploading={isUploading}
                                        />
                                    </div>

                                    <ButtonMain>
                                        Abrir publicados
                                    </ButtonMain>
                                </div>
                            </div>
                        </div>

                        <div style={FileListTabsStyles.divider}></div>

                        {files.length === 0 && (
                            <div>
                                <p className="text-secondary">Nenhum arquivo encontrado na base de conhecimento</p>
                            </div>
                        )}

                        <div className="list-scroll-items">
                            {files.map((file) => (
                                <div
                                    key={file.id}
                                    id="file-list-item"
                                    className="row align-items-center"
                                    style={{
                                        ...FileListTabsStyles.fileListItem,
                                        ...(hoveredFileId === file.id ? FileListTabsStyles.fileListItemHover : {})
                                    }}
                                    onMouseEnter={() => setHoveredFileId(file.id)}
                                    onMouseLeave={() => setHoveredFileId(null)}
                                >
                                    <div className="col-12 col-md-4">
                                        <p>{file.name}</p>
                                        <p style={{ color: '#A3B3FF' }}>
                                            {formatKnowledgeBaseItemStatus(file.origin)}
                                        </p>
                                    </div>

                                    <div
                                        className="col-12 col-md-4 my-3 my-md-0"
                                        style={{ borderRight: '1px solid #314158', position: 'relative', display: 'flex', justifyContent: 'end' }}
                                    >
                                        <div className="d-flex flex-column my-0">
                                            <button
                                                onClick={() => handlePublishClick(file)}
                                                disabled={file.publishable}
                                                className="btn d-flex flex-row gap-2 align-items-center mb-2"
                                                style={{
                                                    backgroundColor: '#C27AFF26',
                                                    color: 'white',
                                                    border: '1px solid #4D179A',
                                                    borderRadius: '8px',
                                                    padding: '4px 16px',
                                                    fontWeight: '500',
                                                }}
                                            >
                                                {file.publishable ? <FaCheck /> : <GoArrowUpRight />}
                                                {file.publishable ? 'Publicado' : 'Publicar'}
                                            </button>

                                            <button
                                                ref={el => actionsDropdownButtonRefs.current[file.id] = el}
                                                onClick={() => setOpenDropdownId(openDropdownId === file.id ? null : file.id)}
                                                style={{ position: 'relative', textAlign: 'right', backgroundColor: 'transparent', border: 'none', color: '#FFF' }}
                                            >
                                                <FaEllipsis />
                                            </button>

                                            <ActionsDropdown
                                                isOpen={openDropdownId === file.id}
                                                onClose={() => setOpenDropdownId(null)}
                                                fileName={file.name}
                                                fileOrigin={file.origin}
                                                isPublished={!!file.publishable}
                                                onDeleteClick={() => handleDeleteActionSelected(file)}
                                                onRenameClick={() => handleRenameClick(file)}
                                                onDownloadClick={() => handleDownloadFile(file.id)}
                                                onRemovePublishableClick={() => handleRemovePublishable(file)}
                                                buttonRef={actionsDropdownButtonRefs.current[file.id]}
                                            />
                                        </div>
                                    </div>

                                    <div className="col-12 col-md-4">
                                        <div className="d-flex flex-row gap-2 align-items-center mb-1">
                                            <MemberAvatar />
                                            <p className="mb-0">{file.processProfile.user.username}</p>
                                        </div>

                                        <p className="text-secondary mb-0" style={{ marginLeft: '38px' }}>{getTimeAgo(file.updatedAt)}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                )}

                {membersActive && (
                    <MembersList projectId={projectId} reloadKey={membersReloadKey} />
                )}
            </div>
        </div>
    )
}